import { ReactNode, useEffect, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { c, msgid } from 'ttag';

import { Pagination, RegionState } from '@freelancelabs/components';

import ListCard from '../../components/ListCard';
import { SortDirection } from '../../graphql/__generated__/schema';
import { AccountsDocument } from '../../graphql/__generated__/types';
import AccountsTable from './AccountsTable';
import { accountsRefusalOf } from './accountsRefusal';

const PAGE_SIZE = 25;

interface Props {
    /** Already-debounced search, wired to `accounts(search:)`. */
    debouncedSearch: string;
    /** Portal filter, wired to `accounts(portalEnabled:)`. */
    portalEnabled: boolean | null;
    moduleTotal: number | null;
    /** The flat view shows the parent company and makes the name sortable. */
    showCompany?: boolean;
    /** Show only independent accounts (root = self alone). */
    independentOnly?: boolean;
    /** Name of the card's scroll region. */
    label: string;
    /** Title and sentence of the unfiltered empty state; each list names its case. */
    emptyTitle: string;
    emptyDescription: ReactNode;
}

/**
 * A flat, paginated, counted list of accounts: the flat view, and the « Comptes
 * indépendants » section of the grouped view. Both query `accounts` with different
 * variables and render the same table.
 *
 * `cache-and-network` because a row's facts change from the sheet; the skeleton keys on
 * the absence of data, not `loading`, else each refetch empties a table being read.
 */
const AccountsList = ({
    debouncedSearch,
    portalEnabled,
    moduleTotal,
    showCompany = false,
    independentOnly = false,
    label,
    emptyTitle,
    emptyDescription,
}: Props) => {
    const [direction, setDirection] = useState<SortDirection>(SortDirection.Asc);
    const [page, setPage] = useState(1);

    // Any criterion change resets to page 1: staying on page 3 of a now shorter result
    // shows an empty table that reads as a bug.
    useEffect(() => setPage(1), [debouncedSearch, portalEnabled, direction]);

    const { data, loading, error } = useQuery(AccountsDocument, {
        variables: {
            search: debouncedSearch || null,
            portalEnabled,
            independentOnly: independentOnly || null,
            direction,
            limit: PAGE_SIZE,
            offset: (page - 1) * PAGE_SIZE,
        },
        fetchPolicy: 'cache-and-network',
    });

    const result = data?.accounts;
    const items = result?.items ?? [];
    // Never `items.length`, which would count a page: the API total follows the search
    // and filter, so it counts what the reader paginates.
    const total = result?.total ?? 0;

    const filtered = Boolean(debouncedSearch) || portalEnabled !== null;
    const showSkeleton = loading && !data;
    const refusal = error ? accountsRefusalOf(error) : null;

    const rangeStart = (page - 1) * PAGE_SIZE + 1;
    const rangeEnd = (page - 1) * PAGE_SIZE + items.length;
    const summary = c('Accounts list').ngettext(
        msgid`${rangeStart}–${rangeEnd} of ${total} account`,
        `${rangeStart}–${rangeEnd} of ${total} accounts`,
        total
    );

    const state = refusal ? (
        // Three states, not two: an error shown as an absence would convince the operator
        // their perimeter is empty, when an empty perimeter is itself an empty page.
        <RegionState
            tone="error"
            title={refusal.title}
            description={
                <>
                    {refusal.guidance}
                    {refusal.detail && (
                        <span className="mt-2 block break-words rounded-md border border-border-norm bg-background-weak p-2 font-mono text-xs">
                            {refusal.detail}
                        </span>
                    )}
                </>
            }
        />
    ) : !showSkeleton && items.length === 0 && filtered ? (
        <RegionState
            title={c('Accounts').t`No account matches these criteria.`}
            description={c('Accounts')
                .t`The search reads the account name and its SIREN, which is missing on about a fifth of the parc, so an account can be there without matching. It does not read the name of the company: search for the subsidiary rather than for the group.`}
        />
    ) : !showSkeleton && items.length === 0 && !filtered ? (
        <RegionState title={emptyTitle} description={emptyDescription} />
    ) : null;

    return (
        <ListCard
            label={label}
            footer={
                !showSkeleton &&
                !error &&
                items.length > 0 && (
                    <div className="px-4">
                        <Pagination
                            page={page}
                            pageSize={PAGE_SIZE}
                            totalCount={total}
                            summary={summary}
                            onPageChange={setPage}
                        />
                    </div>
                )
            }
        >
            <AccountsTable
                rows={items}
                moduleTotal={moduleTotal}
                showCompany={showCompany}
                sortDirection={showCompany ? direction : undefined}
                onSortDirection={showCompany ? setDirection : undefined}
                loading={showSkeleton}
                hasError={Boolean(error)}
                state={state}
            />
        </ListCard>
    );
};

export default AccountsList;
